/*
  Каркас окна поверх страницы: подложка, заголовок, закрытие по Esc и по
  нажатию мимо окна.

  Вынесен из `Confirm` 10.09.2026, когда в журнале загрузок появилось второе
  окно - подробности отказа. Двух копий подложки в кабинете быть не должно:
  закрытие по Esc и остановка всплытия внутри окна расходятся в копиях молча, и
  расхождение находит владелец, а не проверки.

  Содержимое - целиком за вызывающим: у подтверждения две кнопки и вопрос, у
  подробностей одна кнопка и текст, общий у них ровно каркас.
*/

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

// Куда фокус идёт при открытии окна: первое поле ввода (кнопки не берём - см. ниже).
const FIELDS =
  'input:not([type=hidden]):not(:disabled), textarea:not(:disabled), [role=combobox]:not(:disabled)'
// Всё, до чего доходит Tab. По видимости не фильтруем: в среде проверок её нет.
const TABBABLE =
  'button:not(:disabled), a[href], input:not([type=hidden]):not(:disabled), select:not(:disabled), ' +
  'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/*
  `wide` - окно со списком счетов (16.10.2026): выбор счетов перед загрузкой и
  подтверждение удаления. Ширина обычного окна рассчитана на вопрос и две
  кнопки; таблица в нём складывалась бы в столбик. Высота у широкого окна
  ограничена экраном, а длинный список прокручивается внутри (`modal__scroll`),
  чтобы кнопки внизу оставались видны.
*/
/*
  `scroll` - прокручивается окно целиком, а не таблица внутри (26.10.2026):
  окно выбора должников держит в строках выпадающие списки, и прокрутка
  `modal__scroll` обрезала бы раскрытый список своим краем.
*/
/*
  `busy` - запрос из окна в пути (28.10.2026, аудит 5.3): Esc и нажатие мимо
  окна его не закрывают. Иначе человек считал, что отменил, а оплата или
  удаление уже выполнялись, и ошибку запроса некому было показать - окна уже не
  было. Закрыть окно в это время можно только кнопкой, и она тоже погашена.
*/
export default function Modal({
  title,
  onClose,
  note,
  wide = false,
  scroll = false,
  busy = false,
  children,
}) {
  const modifiers = `${wide ? ' modal--wide' : ''}${scroll ? ' modal--scroll' : ''}`
  const close = busy ? undefined : onClose
  const dialog = useRef(null)
  // Кто открыл окно: туда вернётся фокус при закрытии (запоминается до первой отрисовки окна).
  const opener = useRef(typeof document === 'undefined' ? null : document.activeElement)

  useEffect(() => {
    const onKey = (event) => {
      // Esc, уже отработанный раскрытым списком внутри окна, окно не закрывает.
      if (event.key === 'Escape' && !event.defaultPrevented) close?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close])

  /*
    Фокус (аудит 28.10.2026, раздел 5.4). Окно открывалось, а фокус оставался на кнопке под
    ним: Tab ходил по странице под подложкой, читалка экрана не узнавала, что открылось окно.
    Теперь фокус уходит в окно (на первое поле ввода, а без полей - на само окно: первой
    кнопкой часто стоит необратимое «Удалить»), а при закрытии возвращается на открывшую кнопку.
  */
  useEffect(() => {
    const node = dialog.current
    if (node && !node.contains(document.activeElement)) {
      const field = node.querySelector(FIELDS)
      ;(field ?? node).focus({ preventScroll: true })
    }
    const back = opener.current
    return () => {
      if (back?.isConnected) back.focus({ preventScroll: true })
    }
  }, [])

  // Tab не выходит из окна: с последнего элемента - на первый, с первого - на последний.
  const keep = (event) => {
    if (event.key !== 'Tab' || event.defaultPrevented) return
    // Порядок документа задаётся явно: список селекторов не везде отдаёт элементы по порядку.
    const items = [...dialog.current.querySelectorAll(TABBABLE)].sort((left, right) =>
      left.compareDocumentPosition(right) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    )
    if (items.length === 0) {
      event.preventDefault()
      return
    }
    const first = items[0]
    const last = items[items.length - 1]
    const here = document.activeElement
    if (event.shiftKey && (here === first || here === dialog.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && here === last) {
      event.preventDefault()
      first.focus()
    }
  }

  /*
    Через портал в `body`, а не по месту (аудит 28.10.2026, раздел 5.4): подложка начиналась на
    12 точек ниже верха экрана - `position: fixed` привязывался к предку, а не к окну браузера.
    В `body` ни один предок на неё не влияет.
  */
  return createPortal(
    <div className="backdrop" onMouseDown={close}>
      {/*
        Нажатие внутри окна не должно закрывать его: событие всплывает до
        подложки, и без этого окно исчезало бы при попытке выделить текст.
      */}
      <div
        ref={dialog}
        tabIndex={-1}
        className={`modal${modifiers}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={keep}
      >
        <div className="modal__title">{title}</div>
        {/* Строка под заголовком: чем именно была эта загрузка и когда. Без неё
            окно с одним именем файла не отвечает, о какой из строк журнала
            речь, - имена файлов повторяются от волны к волне. */}
        {note ? <div className="modal__note">{note}</div> : null}
        {children}
      </div>
    </div>,
    document.body,
  )
}
